import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Identifier,
    useCanAccess,
    useDataProvider,
    useGetIdentity,
    useGetList,
    useNotify,
} from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import type { CrmDataProvider } from '@/providers/types';
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Paper,
    Stack,
    Typography,
    alpha,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ArchiveIcon from '@mui/icons-material/Archive';
import type { Card, CardStatus } from '@/types';
import { DONE, PENDING, WORKING, formatLimitDate, isOverdue } from './cardStatus';
import { groupCardsByDate } from './cardDateGroups';

const ARCHIVE_PAGE_SIZE = 25;
const ARCHIVE_ACCENT = '#64748b';

interface CardDateListProps {
    /** true: only my open jobs; false: every user's open jobs (superuser "All users" tab). */
    assignedToMe: boolean;
}

export const CardDateList = ({ assignedToMe }: CardDateListProps) => {
    const navigate = useNavigate();
    const notify = useNotify();
    const dataProvider = useDataProvider<CrmDataProvider>();
    const queryClient = useQueryClient();
    const { identity } = useGetIdentity();
    const isAdmin = Boolean((identity as { administrator?: boolean } | undefined)?.administrator);
    const currentUserId = identity?.id as number | undefined;
    const [markingId, setMarkingId] = useState<Identifier | null>(null);

    const { canAccess: canUpdate } = useCanAccess({
        resource: 'cards',
        action: 'edit',
    });

    const [showArchive, setShowArchive] = useState(false);
    const [archivePage, setArchivePage] = useState(1);

    const { data, isPending, error } = useGetList<Card>('cards', {
        filter: { open: true, ...(assignedToMe ? { assigned_to: 'me' } : {}) },
        sort: { field: 'limit_date', order: 'ASC' },
        // shortcut: 500 open cards cap, upgrade to paging if a user ever has more
        pagination: { page: 1, perPage: 500 },
    });

    // Finished jobs (Archive): all DONE cards, newest first, grown 25 at a time.
    const {
        data: archiveData,
        total: archiveTotal,
        isFetching: archiveFetching,
    } = useGetList<Card>(
        'cards',
        {
            filter: { status: DONE, ...(assignedToMe ? { assigned_to: 'me' } : {}) },
            sort: { field: 'id', order: 'DESC' },
            pagination: { page: 1, perPage: ARCHIVE_PAGE_SIZE * archivePage },
        },
        { enabled: showArchive }
    );

    const groups = useMemo(() => groupCardsByDate(data ?? []), [data]);
    const archiveCards = archiveData ?? [];
    const archiveHasMore = (archiveTotal ?? 0) > archiveCards.length;

    const canMarkDone = (card: Card) =>
        !!canUpdate && (isAdmin || card.created_by === currentUserId || card.assigned_to === currentUserId);

    const handleSetStatus = async (card: Card, status: CardStatus) => {
        setMarkingId(card.id);
        try {
            await dataProvider.updateCardStatus(card.id, status);
            // Refetches every active 'cards' query: this list (row drops out) and the admin board.
            await queryClient.invalidateQueries({ queryKey: ['cards'] });
        } catch (err) {
            notify(err instanceof Error ? err.message : 'Failed to update job', {
                type: 'error',
            });
        } finally {
            setMarkingId(null);
        }
    };

    if (isPending) {
        return (
            <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress aria-label="Loading jobs" />
            </Stack>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {error instanceof Error ? error.message : 'Failed to load jobs'}
            </Alert>
        );
    }

    const accentForGroup = (index: number) => {
        const accents = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#ec4899'];
        return accents[index % accents.length];
    };

    const renderCardRow = (card: Card) => {
        const isDoneCard = card.status === DONE;
        const isWorking = card.status === WORKING;
        const overdue = isOverdue(card.limit_date, card.status);
        const details = [
            card.project_name,
            card.field_step_name,
            assignedToMe ? null : card.assigned_to_name,
        ]
            .filter(Boolean)
            .join(' · ');
        const markable = !isDoneCard && canMarkDone(card);

        // Right-side slot (same point as the Archive's Done check): a Working chip, an
        // empty-circle "mark done" button for open cards, or a static green check for Done.
        const workingChip = isWorking ? (
            <Chip
                label="Working"
                size="small"
                color="primary"
                sx={{ height: 22, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
            />
        ) : null;

        const markDoneButton = markable ? (
            <IconButton
                aria-label="Mark as done"
                onClick={() => handleSetStatus(card, DONE)}
                disabled={markingId === card.id}
                sx={{ minWidth: 44, minHeight: 44, color: 'action.active' }}
            >
                <RadioButtonUncheckedIcon />
            </IconButton>
        ) : null;

        // Within the 24h window the backend still returns the Done card here; let the
        // creator/assignee undo completion by clicking the check (reverts to Pending).
        const doneCheck = canMarkDone(card) ? (
            <IconButton
                aria-label="Mark as not done"
                onClick={() => handleSetStatus(card, PENDING)}
                disabled={markingId === card.id}
                sx={{ minWidth: 44, minHeight: 44, color: 'success.main' }}
            >
                <CheckCircleIcon />
            </IconButton>
        ) : (
            <CheckCircleIcon aria-label="Completed" sx={{ color: 'success.main', mr: 1 }} />
        );

        const rightSlot = isDoneCard ? (
            doneCheck
        ) : workingChip || markDoneButton ? (
            <Stack direction="row" alignItems="center" spacing={1}>
                {workingChip}
                {markDoneButton}
            </Stack>
        ) : undefined;

        return (
            <ListItem
                key={card.id}
                divider
                sx={{ pr: isDoneCard ? 8 : rightSlot ? (isWorking && markable ? 16 : 8) : undefined }}
                secondaryAction={rightSlot}
            >
                <ListItemButton onClick={() => navigate(`/cards/${card.id}/show`, { replace: true })}>
                    <ListItemText
                        primary={card.title}
                        secondary={details || null}
                        primaryTypographyProps={
                            isDoneCard
                                ? { sx: { textDecoration: 'line-through', color: 'text.secondary' } }
                                : undefined
                        }
                        secondaryTypographyProps={{ noWrap: true }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            ml: 2,
                            whiteSpace: 'nowrap',
                            color: isDoneCard
                                ? 'success.main'
                                : overdue
                                  ? 'error.main'
                                  : 'text.secondary',
                            fontWeight: overdue && !isDoneCard ? 700 : 400,
                        }}
                    >
                        {isDoneCard
                            ? formatLimitDate(card.due_complete_at ?? null) || 'Done'
                            : formatLimitDate(card.limit_date) || 'No due date'}
                    </Typography>
                </ListItemButton>
            </ListItem>
        );
    };

    const renderColumn = (
        key: string,
        label: string,
        cards: Card[],
        accent: string,
        emptyText: string,
        footer?: ReactNode
    ) => (
        <Paper
            key={key}
            elevation={0}
            sx={{
                flex: 1,
                minWidth: 300,
                p: 2,
                position: 'relative',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: theme => alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(6px)',
                minHeight: 400,
                overflowY: 'auto',
                maxHeight: 'calc(100vh - 300px)',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 3,
                    borderTopLeftRadius: 12,
                    borderTopRightRadius: 12,
                    background: `linear-gradient(90deg, ${accent}, ${alpha(accent, 0.5)})`,
                },
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 2, mt: 0.5 }}
            >
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                        sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: accent,
                            boxShadow: `0 0 0 3px ${alpha(accent, 0.2)}`,
                        }}
                    />
                    <Typography
                        sx={{
                            fontSize: '0.8rem',
                            fontWeight: 700,
                            letterSpacing: 1,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                        }}
                    >
                        {label}
                    </Typography>
                </Stack>
                <Box
                    sx={{
                        px: 1,
                        minWidth: 24,
                        height: 22,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 999,
                        fontSize: '0.72rem',
                        fontWeight: 700,
                        color: accent,
                        bgcolor: alpha(accent, 0.12),
                    }}
                >
                    {cards.length}
                </Box>
            </Stack>

            {cards.length === 0 ? (
                <Box
                    sx={{
                        mt: 2,
                        py: 4,
                        textAlign: 'center',
                        color: 'text.disabled',
                        fontSize: '0.8rem',
                        border: '1px dashed',
                        borderColor: alpha(accent, 0.3),
                        borderRadius: 2,
                    }}
                >
                    {emptyText}
                </Box>
            ) : (
                <List disablePadding>{cards.map(renderCardRow)}</List>
            )}

            {footer}
        </Paper>
    );

    return (
        <Stack spacing={2}>
            <Stack direction="row" justifyContent="flex-end">
                <Button
                    size="small"
                    variant={showArchive ? 'contained' : 'outlined'}
                    startIcon={<ArchiveIcon />}
                    onClick={() => setShowArchive(prev => !prev)}
                >
                    {showArchive ? 'Back to jobs' : 'Archive'}
                </Button>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minHeight: '60vh' }}>
                {showArchive
                    ? renderColumn(
                          'archive',
                          'Archive',
                          archiveCards,
                          ARCHIVE_ACCENT,
                          archiveFetching ? 'Loading…' : 'No finished jobs',
                          archiveHasMore ? (
                              <Button
                                  variant="text"
                                  fullWidth
                                  disabled={archiveFetching}
                                  onClick={() => setArchivePage(prev => prev + 1)}
                                  sx={{ mt: 2, textTransform: 'none', fontWeight: 600, color: ARCHIVE_ACCENT }}
                              >
                                  Load more
                              </Button>
                          ) : null
                      )
                    : groups.map((group, gi) =>
                          renderColumn(group.key, group.label, group.cards, accentForGroup(gi), 'No jobs')
                      )}
            </Stack>
        </Stack>
    );
};

CardDateList.displayName = 'CardDateList';
